import { useEffect, useMemo } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { useGetEventRsvpListQuery } from '@/services/CommunityManagementService';
import { useIntl } from 'react-intl';
import { formatDisplayDateTime } from '@/utils/Date';

interface EventRsvpListProps {
  eventId: string;
}

interface RsvpUser {
  name: string;
  email: string;
  rsvp_created_at: string;
}

const formatDateTime = (dateString: string) => {
  try {
    return formatDisplayDateTime(new Date(dateString));
  } catch {
    return dateString;
  }
};

const EventRsvpList: React.FC<EventRsvpListProps> = ({ eventId }) => {
  const intl = useIntl();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const { data, isLoading, isFetching, isError } = useGetEventRsvpListQuery(
    {
      eventId: eventId || '',
    },
    {
      skip: !eventId,
    }
  );

  useEffect(() => {
    if (data) {
      const totalCount = data.data?.length || 0;
      const calculatedTotalPages = Math.ceil(totalCount / pagination.pageSize);
      handleTotalPages(calculatedTotalPages || 1);
      handleTotalItems(totalCount);
    }
  }, [data, pagination.pageSize, handleTotalPages, handleTotalItems]);

  const rows: RsvpUser[] = useMemo(() => {
    if (!data?.data) return [];
    // Client-side pagination
    const startIndex = pagination.pageIndex * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return data.data.slice(startIndex, endIndex);
  }, [data, pagination.pageIndex, pagination.pageSize]);

  const columns = useMemo(
    () => [
      {
        accessor: (row: RsvpUser) => row.name,
        id: 'name',
        header: intl.formatMessage({ id: 'EVENT.RSVP.TABLE.HEADER.USER_NAME' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: RsvpUser } }) => (
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-light text-primary font-semibold text-sm flex-shrink-0 contrast:text-white">
              {row.original.name
                ? row.original.name.charAt(0).toUpperCase()
                : 'U'}
            </div>
            <span className="font-medium text-gray-800 break-words">
              {row.original.name ||
                intl.formatMessage({ id: 'EVENT.RSVP.TABLE.UNKNOWN_USER' })}
            </span>
          </div>
        ),
      },
      {
        accessor: (row: RsvpUser) => row.email,
        id: 'email',
        header: intl.formatMessage({ id: 'EVENT.RSVP.TABLE.HEADER.EMAIL' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: RsvpUser } }) => (
          <span className="text-gray-800 font-normal break-all">
            {row.original.email}
          </span>
        ),
      },
      {
        accessor: (row: RsvpUser) => row.rsvp_created_at,
        id: 'timestamp',
        header: intl.formatMessage({ id: 'EVENT.RSVP.TABLE.HEADER.TIMESTAMP' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: RsvpUser } }) => (
          <span className="text-gray-800 font-normal whitespace-nowrap">
            {formatDateTime(row.original.rsvp_created_at)}
          </span>
        ),
      },
    ],
    [intl]
  );

  return (
    <div className="card card-grid w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full justify-between">
          <h2 className="card-title">
            {intl.formatMessage({ id: 'EVENT.RSVP.TITLE' })}
          </h2>
          <div className="text-sm text-gray-600">
            {intl.formatMessage(
              { id: 'EVENT.RSVP.INTERESTED_COUNT' },
              { count: totalItems }
            )}
          </div>
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            {intl.formatMessage({ id: 'EVENT.RSVP.LOADING' })}
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-500"
            role="alert"
            aria-live="assertive"
          >
            {intl.formatMessage({ id: 'EVENT.RSVP.ERROR' })}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <KeenIcon icon="information-2" className="text-4xl mb-2" />
            <p>{intl.formatMessage({ id: 'EVENT.RSVP.EMPTY' })}</p>
          </div>
        ) : (
          <div className="rsvp-grid-wrapper">
            <DataGrid<RsvpUser>
              columns={columns}
              data={rows}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: intl.formatMessage({ id: 'COMMON.PAGINATION.INFO' }),
                sizes: [5, 10, 25, 50],
                sizesLabel: intl.formatMessage({
                  id: 'COMMON.PAGINATION.SHOW',
                }),
                sizesDescription: intl.formatMessage({
                  id: 'COMMON.PAGINATION.PER_PAGE',
                }),
              }}
              serverSide={false}
              aria-label="RSVP users table"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventRsvpList;
